import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const nackaPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-nacka",
    serviceCategory: "bortforsling",
    city: "stockholm",

    meta: {
      title: "Bortforsling Nacka – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Nacka. Vi hämtar möbler, vitvaror och skräp i Sickla, Saltsjöbaden, Orminge, Fisksätra, Älta och Boo. Fast pris, ofta samma dag.",
      h1: "Bortforsling Nacka",
    },

    hero: {
      heading: "Bortforsling i Nacka",
      intro:
        "Boka bortforsling i Nacka – vi hämtar möbler, vitvaror och skrymmande föremål i hela kommunen, från lägenheterna i Sickla, Henriksdal och Fisksätra till villorna i Saltsjöbaden, Saltsjö-Duvnäs och Boo. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Över 50 000 slutförda uppdrag i Stockholmsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Nacka – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Nacka",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Nacka och körs till Östervik kretsloppscentral",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Saltsjöbaden, Nacka",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Sickla, Nacka",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Nacka strand, Nacka",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Orminge, Boo, Nacka",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Älta, Nacka",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Nacka – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Nacka är en stor och varierad kommun öster om Stockholm, med kommundelarna Sicklaön, Boo, Älta och Saltsjöbaden-Fisksätra. Bebyggelsen spänner från det täta, snabbväxande Sicklaön – med Sickla, Henriksdal, Jarlaberg, Nacka strand och Kvarnholmen – till de havsnära villorna i Saltsjöbaden och Saltsjö-Duvnäs, de täta lägenhetsområdena i Fisksätra och Orminge, samt villakvarteren i Boo och Älta. Den blandningen gör bortforsling till olika utmaningar: i lägenheten handlar det ofta om trapphus och hiss, medan det i villan snarare handlar om ett fullt garage eller ett källarförråd. Med Ungdomskraft slipper du allt krångel oavsett vilket. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, villa eller ditt förråd, lastar i bilen och kör till närmaste kretsloppscentral. Här har Nacka en stor fördel – kommunens eget bolag Nacka vatten och avfall driver tre kretsloppscentraler: Östervik, Boo och Älta. Det betyder att vi nästan alltid har en återvinningscentral nära din adress, oavsett kommundel. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att Värmdöleden går genom Nacka och Saltsjöbanan binder ihop kommunen från Slussen ut till Saltsjöbaden tar vi oss snabbt mellan kommundelarna – och vi planerar runt flaskhalsar som Skurubron till Boo. Det gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid både höghusen i Fisksätra och de havsnära villatomterna i Saltsjöbaden – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Nacka",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa i Saltsjöbaden, Saltsjö-Duvnäs, Skuru eller Boo är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas. Bor du i lägenhet i Sickla, Henriksdal, Fisksätra eller Orminge, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller förrådet. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som Nackabo på någon av kommunens kretsloppscentraler i Östervik, Boo eller Älta.",
      additionalSections: [
        {
          heading: "Kommundelar och områden vi täcker i Nacka",
          content:
            "Vi utför bortforsling i hela Nacka kommun – från det täta Sicklaön till de havsnära villaområdena. Oavsett om du bor i en lägenhet i Sickla, en villa i Saltsjöbaden, ett radhus i Älta eller en nybyggd etta i Nacka strand kan vi vara på plats, ofta redan samma dag.\n\nPå Sicklaön – med Sickla köpkvarter, Nacka Forum, Finntorp, Ektorp, Jarlaberg, Henriksdal, Kvarnholmen och Nacka strand – har vi stor vana vid lägenhetshämtningar med hiss och trapphus, både i äldre kvarter och i de nybyggda höghusen vid vattnet. I Saltsjöbaden och Saltsjö-Duvnäs möter vi havsnära villor med stora tomter, och i Fisksätra de täta flerbostadshusen.\n\nVi är minst lika snabba i kommunens övriga delar: Boo med Orminge centrum, Saltsjö-Boo, Kummelnäs och nya Tollare, samt Älta i söder mot Nackareservatet och Hellasgården. Tack vare närheten till Värmdöleden, Saltsjöbanan och tvärbanan vid Sickla planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för någon kommundel.",
        },
        {
          heading: "Återvinning och miljöansvar i Nacka",
          content:
            "En viktig del av vår bortforsling i Nacka är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända kretsloppscentraler. Nacka har en stor fördel eftersom kommunens eget bolag Nacka vatten och avfall driver tre stationer på olika sidor av kommunen:\n\nÖstervik kretsloppscentral – nära Saltsjöbaden och Fisksätra, vid Saltsjöbanan, där grovavfall, vitvaror, elavfall, farligt avfall och återbruk tas emot. Boo kretsloppscentral – smidig för uppdrag i Orminge, Saltsjö-Boo och Kummelnäs. Älta kretsloppscentral – för uppdrag i de södra delarna. Vi väljer alltid den station som ligger närmast din adress för att hålla restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Myrorna och Stadsmissionen. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Nacka med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Nackas kommundelar, från höghusen i Sickla och Fisksätra till villatomterna i Saltsjöbaden och Boo, och vet att kommunens tre kretsloppscentraler ligger spridda nära dig. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Stockholmsregionen och Värmdöleden går genom Nacka. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för någon kommundel i Nacka, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via Nackas kretsloppscentraler i Östervik, Boo och Älta. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Nacka?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för någon kommundel i Nacka.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Nacka?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Nacka?",
        answer:
          "Vi har skåpbilar och personal i hela Stockholmsregionen och Värmdöleden går genom Nacka, så vi tar oss snabbt mellan kommundelarna. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Nacka?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste kretsloppscentral.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Nacka?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i flerbostadshusen kring Sickla, Henriksdal, Fisksätra och Orminge. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Hämtar ni från villa och garage i Nacka?",
        answer:
          "Ja, en stor del av våra uppdrag i Nacka är villahämtningar i Saltsjöbaden, Saltsjö-Duvnäs, Skuru och Boo. Vi backar in på uppfarten där det går och tömmer garage, källare och förråd. Meddela oss bara om en grind eller garageport behöver öppnas.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Nacka försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Nacka?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända kretsloppscentral. Nacka vatten och avfall driver tre stationer – Östervik, Boo och Älta – så det finns alltid en nära din adress. Det som kan återanvändas skänks till Myrorna eller Stadsmissionen.",
      },
      {
        question: "Kan jag boka bortforsling i Nacka på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Nacka?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid både villaförråden i Saltsjöbaden och Boo och lägenhetsförråden i Sickla och Fisksätra.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Nacka?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet i Sickla och ditt förråd på en annan adress i kommunen.",
      },
      {
        question: "Vilka kommundelar i Nacka täcker ni?",
        answer:
          "Vi utför bortforsling i hela Nacka – Sicklaön (Sickla, Henriksdal, Jarlaberg, Nacka strand, Kvarnholmen, Saltsjö-Duvnäs, Skuru), Boo (Orminge, Saltsjö-Boo, Kummelnäs, Tollare), Älta samt Saltsjöbaden och Fisksätra. Inga extra avgifter för någon kommundel.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Nacka?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som Nackabo på kommunens kretsloppscentraler i Östervik, Boo eller Älta.",
      },
      {
        question: "Vilka kommuner runt Nacka utför ni bortforsling i?",
        answer: `Vi täcker hela Stockholmsregionen, inklusive följande kommuner: ${stockholmKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Nacka i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Nacka kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Nacka idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Nacka kommun.",
    },

    en: {
      slug: "junk-removal-nacka",
      meta: {
        title: "Junk Removal Nacka – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Nacka. We pick up furniture, appliances and bulky items in Sickla, Saltsjöbaden, Orminge, Fisksätra, Älta and Boo. Fixed price, often same day.",
        h1: "Junk Removal Nacka",
      },
      hero: {
        heading: "Junk Removal in Nacka",
        intro:
          "Book junk removal in Nacka – we collect furniture, appliances and bulky items across the whole municipality, from the apartments in Sickla, Henriksdal and Fisksätra to the villas in Saltsjöbaden, Saltsjö-Duvnäs and Boo. Quick booking, fixed price and often available the same day. Over 50,000 completed jobs in the Stockholm region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Nacka – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Nacka",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Nacka and driven to Östervik recycling centre",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Saltsjöbaden, Nacka",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Sickla, Nacka",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Nacka strand, Nacka",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Orminge, Boo, Nacka",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Älta, Nacka",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Nacka – fast, hassle-free and responsible",
        mainText:
          "Nacka is a large and varied municipality east of Stockholm, with the districts of Sicklaön, Boo, Älta and Saltsjöbaden-Fisksätra. The housing ranges from the dense, fast-growing Sicklaön – with Sickla, Henriksdal, Jarlaberg, Nacka strand and Kvarnholmen – to the seaside villas of Saltsjöbaden and Saltsjö-Duvnäs, the dense apartment areas of Fisksätra and Orminge, and the villa neighbourhoods of Boo and Älta. That mix makes junk removal a different challenge: in an apartment it is often stairwells and elevators, while in a villa it is more about a full garage or a basement storage room. With Ungdomskraft, you skip the hassle either way. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your apartment, villa or storage unit, load them into the van and drive to the nearest recycling centre. Here Nacka has a big advantage – the municipality's own company, Nacka vatten och avfall, runs three recycling centres (kretsloppscentraler): Östervik, Boo and Älta. This means we almost always have a recycling centre near your address, whatever the district. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause Värmdöleden runs through Nacka and the Saltsjöbanan railway links the municipality from Slussen out to Saltsjöbaden, we move quickly between districts – and we plan around bottlenecks like the Skuru bridge to Boo. This means we can often be on site the same day you book. Our workers are used to both the high-rises of Fisksätra and the seaside villa plots of Saltsjöbaden – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Nacka",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa in Saltsjöbaden, Saltsjö-Duvnäs, Skuru or Boo, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening. If you live in an apartment in Sickla, Henriksdal, Fisksätra or Orminge, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use.\n\nGather the items in one place if possible, for example in the hallway, garage or storage unit. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Nacka resident you can drop those off for free at one of the municipality's recycling centres in Östervik, Boo or Älta.",
        additionalSections: [
          {
            heading: "Districts and areas we cover in Nacka",
            content:
              "We perform junk removal across the entire municipality of Nacka – from the dense Sicklaön to the seaside villa areas. Whether you live in an apartment in Sickla, a villa in Saltsjöbaden, a townhouse in Älta or a new studio in Nacka strand, we can be on site, often the same day.\n\nOn Sicklaön – with Sickla köpkvarter, Nacka Forum, Finntorp, Ektorp, Jarlaberg, Henriksdal, Kvarnholmen and Nacka strand – we are highly experienced with apartment pickups via elevator and stairwell, both in older quarters and in the newly built high-rises by the water. In Saltsjöbaden and Saltsjö-Duvnäs we meet seaside villas with large plots, and in Fisksätra the dense apartment buildings.\n\nWe are just as quick in the other parts of the municipality: Boo with Orminge centre, Saltsjö-Boo, Kummelnäs and the new Tollare, and Älta to the south towards the Nacka nature reserve and Hellasgården. Thanks to the proximity of Värmdöleden, the Saltsjöbanan and the tvärbanan tram at Sickla, we always plan the route in advance so you don't have to wait. No extra charges for any district.",
          },
          {
            heading: "Recycling and environmental responsibility in Nacka",
            content:
              "An important part of our junk removal in Nacka is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres. Nacka has a big advantage because the municipality's own company, Nacka vatten och avfall, runs three stations on different sides of the municipality:\n\nÖstervik recycling centre – near Saltsjöbaden and Fisksätra, by the Saltsjöbanan, which receives bulky waste, white goods, e-waste, hazardous waste and reusable items. Boo recycling centre – convenient for jobs in Orminge, Saltsjö-Boo and Kummelnäs. Älta recycling centre – for jobs in the southern parts. We always choose the station nearest your address to keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Myrorna and Stadsmissionen. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Nacka with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Nacka's districts, from the high-rises of Sickla and Fisksätra to the villa plots of Saltsjöbaden and Boo, and that the municipality's three recycling centres are spread out close to you. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Stockholm region and Värmdöleden runs through Nacka. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any district in Nacka, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via Nacka's recycling centres in Östervik, Boo and Älta. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Nacka cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any district in Nacka.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Nacka?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Nacka?",
          answer:
            "We have vans and staff across the Stockholm region and Värmdöleden runs through Nacka, so we move quickly between districts. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Nacka?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Nacka?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the apartment buildings around Sickla, Henriksdal, Fisksätra and Orminge. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Do you collect from villas and garages in Nacka?",
          answer:
            "Yes, a large share of our jobs in Nacka are villa pickups in Saltsjöbaden, Saltsjö-Duvnäs, Skuru and Boo. We reverse onto the driveway where possible and clear garages, basements and storage rooms. Just let us know if a gate or garage door needs opening.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Nacka insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Nacka?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. Nacka vatten och avfall runs three stations – Östervik, Boo and Älta – so there is always one near your address. Items that can be reused are donated to Myrorna or Stadsmissionen.",
        },
        {
          question: "Can I book junk removal in Nacka on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Nacka?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage rooms, attics and garages. We are used to both the villa storage rooms in Saltsjöbaden and Boo and the apartment storage units in Sickla and Fisksätra.",
        },
        {
          question: "Can you make multiple stops during junk removal in Nacka?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment in Sickla and a storage unit at another address in the municipality, for example.",
        },
        {
          question: "Which districts in Nacka do you cover?",
          answer:
            "We perform junk removal across all of Nacka – Sicklaön (Sickla, Henriksdal, Jarlaberg, Nacka strand, Kvarnholmen, Saltsjö-Duvnäs, Skuru), Boo (Orminge, Saltsjö-Boo, Kummelnäs, Tollare), Älta, and Saltsjöbaden and Fisksätra. No extra charges for any district.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Nacka?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Nacka resident you can drop those off for free at the municipality's recycling centres in Östervik, Boo or Älta.",
        },
        {
          question: "Which municipalities around Nacka do you serve?",
          answer:
            "We cover the entire Stockholm region, including Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Nacka in 3 easy steps",
        step2Text:
          "Our young workers in Nacka come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Nacka today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole municipality of Nacka.",
      },
    },
  },
];
